export interface Provider {
  name: string;
  label: string;
  type: 'backend' | 'browser';
  disabled?: boolean;
}

export interface ProvidersResponse {
  stt: Provider[];
  tts: Provider[];
}

export interface SttMetrics {
  provider: string;
  processingTimeMs: number;
  audioDurationMs: number;
  rtf: number;
  cost: number;
}

export interface TtsMetrics {
  provider: string;
  processingTimeMs: number;
  characterCount: number;
  cost: number;
}

export interface LlmMetrics {
  model?: string;
  processingTimeMs: number;
  inputTokens: number;
  cachedInputTokens?: number;
  outputTokens: number;
  cost: number;
}

export interface ConversationMetrics {
  stt?: SttMetrics;
  llm: LlmMetrics;
  tts?: TtsMetrics;
  totalLatencyMs: number;
  totalCost: number;
  wer?: number;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  metrics?: ConversationMetrics;
  timestamp: Date;
}

// ── MCP Server Config ──

export interface McpAuthConfig {
  type: 'client_credentials' | 'none';
  clientId?: string;
  clientSecret?: string;
}

export interface McpServerConfig {
  name: string;
  url: string;
  headers?: Record<string, string>;
  auth?: McpAuthConfig;
  // Tool arg → requestContext key: the arg is hidden from the model and injected server-side.
  contextArgs?: Record<string, string>;
  // Tool names dropped from this server before exposing them to the agent.
  hiddenTools?: string[];
}

// ── Agent Template (admin) ──

export interface CustomFieldDef {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  required: boolean;
  placeholder?: string;
  options?: string[];
}

export interface AgentTemplate {
  id: string;
  name: string;
  description?: string;
  greeting?: string;
  sttProvider: string;
  ttsProvider: string;
  llmModel: string;
  baseSystemPrompt: string;
  language: string;
  voiceMode?: 'pipeline' | 'realtime';
  customFields: CustomFieldDef[];
  isPublic: boolean;
  createdAt: string;
}

// ── Catalog (public) ──

export interface CatalogTemplate {
  id: string;
  name: string;
  description?: string;
  language: string;
  customFields: CustomFieldDef[];
}

// ── Agent (user's instance) ──

/** WhatsApp Business (Meta Cloud API) credentials for this agent's number. */
export interface WhatsappConfig {
  phoneNumberId: string;
  accessToken: string;
  /** Echoed back during Meta's webhook verification handshake. */
  verifyToken?: string;
  /** Meta app secret — validates the signature on inbound webhooks. */
  appSecret?: string;
  apiVersion?: string;
}

/** Eazyshow bridge: mirrors the conversation to operators and lets them take over. */
export interface EazyshowConfig {
  baseUrl: string;
  s2sToken: string;
  accountId: string;
  /** Bearer the Eazyshow instance sends on operator webhooks. */
  webhookToken: string;
  groupId?: string;
  subgroupId?: string;
}

/** MCP tool that stores a photo the customer sent, so other tools can act on it. */
export interface MediaUploadConfig {
  toolName: string;
  imageArg?: string;
  sessionArg?: string;
  extraArgs?: Record<string, string>;
}

export interface Agent {
  id: string;
  templateId: string;
  apiKey: string;
  name: string;
  customPrompt?: string;
  customFieldValues: Record<string, string>;
  mcpServers?: McpServerConfig[];
  maxHistoryMessages?: number;
  greeting?: string;
  detailedMetrics?: boolean;
  // Overrides the template's voice mode; null/undefined = inherit the template.
  voiceMode?: 'pipeline' | 'realtime' | null;
  knowledgeBaseIds?: string[];
  whatsapp?: WhatsappConfig;
  eazyshow?: EazyshowConfig;
  mediaUpload?: MediaUploadConfig;
  createdAt: string;
}

export interface AgentPublicInfo {
  name: string;
  description?: string;
  language: string;
  useStreaming?: boolean;
}

export interface ConnectResponse {
  sessionId: string;
  agent: AgentPublicInfo;
}

export interface TextChatResult {
  response: string;
  metrics: {
    llmLatencyMs: number;
    inputTokens: number;
    outputTokens: number;
    cost: number;
  };
}

// ── Workspace ──

export interface Workspace {
  id: string;
  name: string;
  apiKey: string;
  templateIds: string[];
  createdAt: string;
}

// ── Session ──

export interface TurnMetrics {
  sttLatencyMs?: number;
  llmLatencyMs?: number;
  ttsLatencyMs?: number;
  totalLatencyMs: number;
  totalCost: number;
  stt?: SttMetrics;
  llm?: LlmMetrics;
  tts?: TtsMetrics;
  wer?: number;
}

export enum SessionSource {
  Direct = 'direct',
  Twilio = 'twilio',
  WhatsApp = 'whatsapp',
}

export enum SessionMode {
  Voice = 'voice',
  Text = 'text',
}

export interface SessionMessage {
  role: 'user' | 'assistant';
  text: string;
  timestamp: string;
  metrics?: TurnMetrics;
}

export interface SessionContext {
  callSid?: string;
  callerPhone?: string;
  callbackUrl?: string;
  consumerContext?: string;
}

export interface SessionSummary {
  id: string;
  agentId: string;
  templateId: string;
  source: SessionSource;
  mode?: SessionMode;
  status: 'active' | 'ended';
  context?: SessionContext;
  messages: SessionMessage[];
  createdAt: string;
  updatedAt: string;
}
